/**
 * ProfileViewers — "Who viewed your profile" widget for the current
 * user. Renders on the self-profile page. Data: GET /api/network/profile-views
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Eye, Users } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const ProfileViewers = () => {
  const { API } = useContext(AuthContext);
  const [viewers, setViewers] = useState([]);
  const [total, setTotal]     = useState(0);
  const [ready, setReady]     = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/network/profile-views`)
      .then((r) => {
        if (!alive) return;
        setViewers(r.data?.viewers || []);
        setTotal(r.data?.total_views || 0);
      })
      .catch(() => {})
      .finally(() => alive && setReady(true));
    return () => { alive = false; };
  }, [API]);

  if (!ready) return null;

  return (
    <aside className="pro-card p-4" data-testid="profile-viewers">
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2">
          <Eye className="w-4 h-4 text-primary" />
          <span className="font-heading font-extrabold text-sm">Who viewed your profile</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          {total} view{total === 1 ? '' : 's'} · 30d
        </span>
      </div>
      {viewers.length === 0 ? (
        <div className="text-xs text-muted-foreground italic inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> No profile views yet. Keep posting to get discovered.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="profile-viewers-list">
          {viewers.slice(0, 5).map((v) => (
            <li key={v.id} className="flex items-center gap-2" data-testid={`profile-viewer-${v.id}`}>
              <Link
                to={`/u/${v.handle || v.id}`}
                className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden"
              >
                {v.avatar_url
                  ? <img src={v.avatar_url} alt="" className="h-full w-full object-cover" />
                  : (v.name || '?').slice(0, 2).toUpperCase()}
              </Link>
              <div className="min-w-0 flex-1">
                <Link to={`/u/${v.handle || v.id}`} className="text-xs font-semibold truncate block hover:text-primary transition-colors">
                  {v.name}
                </Link>
                <div className="text-[10px] text-muted-foreground truncate">
                  {v.views > 1 ? `${v.views} views · ` : ''}
                  {(() => { try { return formatDistanceToNow(parseISO(v.last_viewed_at), { addSuffix: true }); } catch { return ''; } })()}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};

export default ProfileViewers;
